import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Container, Row, Col, Button, Card } from "react-bootstrap";
import googlePlayImg from "../../assets/img/mobile_app/get_it_on_google_play.png";
import appStoreImg from "../../assets/img/mobile_app/download_on_the_app_store.png";
import { SectionCard, LoadingDots, ServerDown } from "../../components/general";
import { getHomePageContent } from "../../services/publicPages";
import { GOOGLE_PLAY_APP, APP_STORE_APP } from "../../constants/constants";
import { SIGNUP_ROUTE } from "../../routes/RoutesPathes";
import styles from "./home.module.css";

export const Home = () => {
  const { t } = useTranslation();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverUnavailable, setServerUnavailable] = useState(false);

  // fetch home page content from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getHomePageContent();
        setData(data);
        setLoading(false);
      } catch (err) {
        if (!err?.response) {
          setServerUnavailable(true);
        }
      }
    };

    fetchData();
  }, []);

  return (
    <>
      {serverUnavailable ? (
        <ServerDown />
      ) : loading ? (
        <LoadingDots />
      ) : (
      <>
        {/* Hero Section */}
        <section className={`section ${styles.hero}`}>
          <Container>
            <Row className="align-items-center justify-content-between g-4">
              {/* Left Column */}
              <Col lg={6} className="order-2 order-lg-1 d-flex flex-column justify-content-center">
                <h2 className="s-fs-2">{data.section1Title}</h2>
                <p className="section-text">{data.section1Description}</p>
                <div className="d-flex">
                  <Button 
                    as={Link}
                    to={SIGNUP_ROUTE}
                    variant="primary"
                  >
                    {t("buttons.join_now")}
                  </Button>
                </div>
              </Col>

              {/* Right Column */}
              <Col lg={5} className="order-1 order-lg-2 text-center">
                <img src={data.section1Image} className={`img-fluid ${styles.heroImg}`} alt="" />
              </Col>
            </Row>
          </Container>
        </section> {/* /Hero Section */}

        {/* Mobile App Section */}
        <section className={`section ${styles.app}`}>
          <SectionCard bgColor="var(--s-gray-color)">
            <Row className="gy-4 align-items-center justify-content-center">
              {/* Left Column */}
              <Col 
                lg={6}
                className="order-2 order-lg-1 d-flex flex-column justify-content-center"
              >
                <h3 className="s-fs-3">{data.section2Title}</h3>
                <p>{data.section2Description}</p>
              </Col>

              {/* Right Column */}
              <Col lg={5} className="order-1 order-lg-2 text-center">
                <div className={styles.mobileImgBox}>
                  <img src={data.section2Image} className={styles.mobileImg} alt="sabeqoon mobile app screen" />
                </div>
              </Col>
            </Row>
          </SectionCard>

          <SectionCard bgColor="var(--s-gray-color)">
            <Row className="gy-4 align-items-center justify-content-center">
              {/* Left Column */}
              <Col 
                lg={5}
                className="order-1 order-lg-1 text-center"
              >
                <div className={styles.mobileImgBox}>
                  <img src={data.section3Image} className={styles.mobileImg} alt="sabeqoon mobile app screen" />
                </div>
              </Col>

              {/* Right Column */}
              <Col lg={6} className="order-2 order-lg-2 d-flex flex-column justify-content-center">
                <h3 className="s-fs-3">{data.section3Title}</h3>
                <p>{data.section3Description}</p>
              </Col>
            </Row>
          </SectionCard>
        </section> {/* /Mobile App Section */}

        {/* Mobile App Section */}
        <section className={`section mb-5 ${styles.downloadSection}`}>
          <Container className={styles.downloadSectionContainer}>
            <h3 className="s-fs-3">{data.section4Title}</h3>
            <p className={styles.description}>{data.section4Description}</p>
            <div className={styles.buttonGroup}>
            <a href={GOOGLE_PLAY_APP} target="_blank" rel="noopener noreferrer">
              <img src={googlePlayImg} className={styles.getAppImg} alt="get it on google play" />
            </a>
            <a href={APP_STORE_APP} target="_blank" rel="noopener noreferrer">
              <img src={appStoreImg} className={styles.getAppImg} alt="download it on app store" />
            </a>
            </div>
          </Container>
        </section> {/* /Mobile App Section */}

        {/* Solutions section */}
        <section className={`section mb-5 ${styles.solution} ${styles.backgroundImage}`}>
          <Container fluid>
            <Container>
              <h3 className="fw-bold text-center mb-5">{data.section5Title}</h3>
              <Row>
                <Col className="d-flex justify-content-center text-center">
                  <Card className={`s-bg-yellow ${styles.cardSolution}`}>
                    <Card.Body className={`${styles.cardBody}`}>
                      <Card.Img variant="top" src={data.section5Card1Icon} className={styles.solutionIcon}/>
                      <Card.Title className={styles.title}>
                        {data.section5Card1Title}
                      </Card.Title>
                      <Card.Text>
                        {data.section5Card1Description}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>

                <Col className="d-flex justify-content-center text-center">
                  <Card className={`s-bg-purple ${styles.cardSolution}`}>
                    <Card.Body className={`${styles.cardBody}`}>
                      <Card.Img variant="top" src={data.section5Card2Icon} className={styles.solutionIcon}/>
                      <Card.Title className={styles.title}>
                        {data.section5Card2Title}
                      </Card.Title>
                      <Card.Text>
                        {data.section5Card2Description}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>

                <Col className="d-flex justify-content-center text-center">
                  <Card className={`s-bg-fuchia ${styles.cardSolution}`}>
                    <Card.Body className={`${styles.cardBody}`}>
                      <Card.Img variant="top" src={data.section5Card3Icon} className={styles.solutionIcon}/>
                      <Card.Title className={styles.title}>
                        {data.section5Card3Title}
                      </Card.Title>
                      <Card.Text>
                        {data.section5Card3Description}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>

                <Col className="d-flex justify-content-center text-center">
                  <Card className={`s-bg-black ${styles.cardSolution}`}>
                    <Card.Body className={`${styles.cardBody}`}>
                      <Card.Img variant="top" src={data.section5Card4Icon} className={styles.solutionIcon}/>
                      <Card.Title className={styles.title}>
                        {data.section5Card4Title}
                      </Card.Title>
                      <Card.Text>
                        {data.section5Card4Description}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Container>
          </Container>
        </section> {/* /Solutions Section */}

      </>
      )}
    </>
  )
}
